import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, MapPinIcon } from 'lucide-react';
import { profile } from '../data/profile';
import { PESeal } from '../components/PESeal';
import { industryMarks } from '../components/IndustryIcons';
import { FocalQuote, Metric, Panel } from '../components/SectionFrame';
const boardMetrics = [{
  value: '$2B',
  label: 'Annual Regulated Revenue',
  detail: 'Three operating companies under one technology strategy.'
}, {
  value: '1.3M+',
  label: 'Customer Relationships',
  detail: 'Electric and natural gas service across Florida and New Mexico.'
}, {
  value: '25+',
  label: 'Years In Critical Infrastructure',
  detail: 'Engineering, operations, capital projects, and enterprise technology.'
}, {
  value: '320+',
  label: 'Technology Organization',
  detail: 'Leaders, engineers, and operators across IT and OT.'
}];
export function Home() {
  return <div className="mx-auto w-full max-w-sheet px-5 sm:px-8" data-id="element-190" data-mp="3owcejc9j541q">
      {/* SECTION 1 — HERO / EXECUTIVE BOARDROOM SUMMARY */}
      <section className="pt-12 lg:pt-16" aria-labelledby="hero-headline" data-id="element-191" data-mp="30vgmijyiqvq0">
        <div className="flex items-baseline gap-4" data-id="element-192" data-mp="2vx6taewcu571">
          <span className="tabular display text-2xl font-black text-copper-light" data-id="element-193" data-mp="2vvlw0hino85h">01</span>
          <span className="text-[11px] font-semibold uppercase tracking-[0.28em] text-hydro-light" data-id="element-194" data-mp="2qwshqmvjod49">
            Executive Boardroom Summary
          </span>
        </div>

        <div className="mt-6 grid gap-10 lg:grid-cols-[1.55fr_1fr] lg:gap-14" data-id="element-195" data-mp="2hyywq838d5we">
          <div data-id="element-196" data-mp="2d6ozs83p75km">
            <h1 id="hero-headline" className="display text-[2.1rem] font-black leading-[1.04] text-panel sm:text-5xl lg:text-[3.5rem]" data-id="element-197" data-mp="2d65sdlbobzku">
              David Lukcic, P.E. — Board Director & Enterprise CIO: Governing $100M+ Tech Transformation in $10B+
              Critical Infrastructure
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-panel/75 lg:text-lg" data-id="element-198" data-mp="1z98tcwlejbsv">
              25+ years operating at the intersection of technology, operational risk, capital allocation, and
              regulatory trust across regulated operating companies — Tampa Electric, Peoples Gas, and New Mexico Gas.
              He builds the case for capital investment, modernizes the systems that keep the lights and gas on for
              millions of people, and translates cyber and AI risk into decisions boards can act on.
            </p>

            <div className="mt-7 flex flex-wrap items-center gap-3" data-id="element-199" data-mp="23cjg79vlvzm7">
              <Link to="/contact" className="group inline-flex items-center gap-2 bg-copper px-6 py-3 text-sm font-semibold uppercase tracking-[0.14em] text-panel transition-colors duration-150 ease-exec hover:bg-copper-light hover:text-steel-dark" data-id="element-200" data-mp="28atbrll8t28c">
                Board & Advisory Engagement
                <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-exec group-hover:translate-x-1" data-id="element-201" data-mp="2m8hyz02s8lgk" />
              </Link>
              <Link to="/stride" className="inline-flex items-center gap-2 border border-white/25 px-6 py-3 text-sm font-semibold uppercase tracking-[0.14em] text-panel transition-colors duration-150 ease-exec hover:border-copper-light hover:text-copper-light" data-id="element-202" data-mp="fltz4vddfqe4">
                Review the STRIDE Playbook
              </Link>
            </div>

            <ul className="mt-9 grid gap-x-8 gap-y-4 border-t border-white/10 pt-7 sm:grid-cols-2" data-id="element-203" data-mp="3ssc9uxarocy0">
              {industryMarks.map(({
              key,
              label,
              Icon
            }) => <li key={key} className="flex items-center gap-3 text-sm text-panel/80" data-id="element-204" data-mp="3nutzd2h3ppnw">
                  <Icon className="h-7 w-7 shrink-0 text-hydro-light" data-id="element-205" data-mp="5oflt4mqvhq1" />
                  {label}
                </li>)}
            </ul>
          </div>

          <div className="flex flex-col gap-6" data-id="element-206" data-mp="1h29xzaify50h">
            <figure className="relative border-4 border-copper-light bg-panel p-3 shadow-2xl" data-id="element-207" data-mp="1h0yl8l70pmx8">
              <img src={profile.headshot} alt="Portrait of David Lukcic, Chief Information and Technology Officer" className="h-auto w-full object-cover" data-id="element-208" data-mp="1c3gk3gz8sbzs" />
              <figcaption className="flex items-baseline justify-between gap-3 px-1 pt-3 text-steel-dark" data-id="element-209" data-mp="2ceszlx5kgji2">
                <span className="display text-base font-black" data-id="element-210" data-mp="2hef9bwm6rhm4">{profile.name}</span>
                <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-copper" data-id="element-211" data-mp="2m92t5i68vnud">
                  {profile.title}
                </span>
              </figcaption>
            </figure>

            <div className="flex items-center gap-5 border border-white/10 bg-steel-dark/80 p-5" data-id="element-212" data-mp="2v9heuin4f4ye">
              <PESeal className="h-24 w-24 shrink-0" data-id="element-213" data-mp="304wqd3xnoorn" />
              <div data-id="element-214" data-mp="350lu7jgpkrtr">
                <p className="display text-lg font-black leading-tight text-panel" data-id="element-215" data-mp="3513viui89w8j">Licensed Professional Engineer</p>
                <p className="mt-1 text-sm leading-relaxed text-panel/70" data-id="element-216" data-mp="39yne26lykv6a">
                  State of Florida — an engineer&apos;s discipline applied to what is actually achievable versus what is
                  merely a good slide.
                </p>
              </div>
            </div>

            <p className="flex items-center gap-2 text-sm text-panel/70" data-id="element-217" data-mp="yc3ewfysowum">
              <MapPinIcon className="h-4 w-4 text-copper-light" data-id="element-218" data-mp="tf37o1q7lo1x" /> {profile.location}
            </p>
          </div>
        </div>

        <div className="mt-12 grid gap-px bg-white/10 sm:grid-cols-2 lg:grid-cols-4" data-id="element-219" data-mp="ohbgxb1aw7zb">
          {boardMetrics.map(m => <Metric key={m.label} {...m} data-id="element-220" data-mp="1h44k0025xjch" />)}
        </div>

        <figure className="mt-12" data-id="element-221" data-mp="1h5z4xedfpbgk">
          <img src={profile.banner} alt="Executive banner: David Lukcic, P.E., MBA — Chief Information Officer. Core expertise in AI and Responsible AI, cybersecurity governance, board and CEO advisory, M&A and TSA integration, leadership development, cloud and OT modernization." className="w-full border border-white/10 object-cover" data-id="element-222" data-mp="1ccw6osxppdu1" />
          <figcaption className="mt-3 text-[11px] uppercase tracking-[0.2em] text-panel/45" data-id="element-223" data-mp="3uaklk8r8omau">
            Executive profile banner — core expertise and enterprise scorecard
          </figcaption>
        </figure>

        <FocalQuote data-id="element-224" data-mp="3kl10qxjyuidc">
          Technology at the board level is not a cost center to manage—it is the ultimate strategic lever to protect the
          balance sheet, earn regulatory trust, and compound enterprise value.
        </FocalQuote>
      </section>

      {/* ABOUT THIS PORTFOLIO */}
      <section className="mt-16 border-t border-white/10 pt-12 lg:mt-20" aria-labelledby="about-headline" data-id="element-225" data-mp="3flg4iuw6ko4b">
        <div className="flex items-baseline gap-4" data-id="element-226" data-mp="31mgfhm4ra77m">
          <span className="tabular display text-2xl font-black text-copper-light" data-id="element-227" data-mp="31nrhbgen54w3">00</span>
          <span className="text-[11px] font-semibold uppercase tracking-[0.28em] text-hydro-light" data-id="element-228" data-mp="36ljjr4cww9ej">
            About This Portfolio
          </span>
        </div>
        <h2 id="about-headline" className="display mt-4 max-w-4xl text-3xl font-black leading-[1.1] text-panel lg:text-[2.6rem]" data-id="element-229" data-mp="14twr6yddg6fy">
          A Board-Ready Record, Structured for Directors and Nominating Committees
        </h2>
        <p className="mt-4 max-w-3xl text-base leading-relaxed text-panel/70 lg:text-lg" data-id="element-230" data-mp="19zs1rdwqsdog">
          Each section stands alone: governance and capital allocation, realized enterprise value, mega-project
          delivery, AI and cyber resilience, culture, and engagement terms — no restatement, no filler.
        </p>

        <div className="mt-9 grid gap-6 lg:grid-cols-3" data-id="element-231" data-mp="1xm8fcw3p295h">
          <Panel className="p-7 lg:col-span-2" data-id="element-232" data-mp="1sprohu541gop">
            <h3 className="display text-xl font-black text-steel-dark" data-id="element-233" data-mp="1sr2rvsun8mfa">Who this is written for</h3>
            <p className="mt-3 text-[15px] leading-relaxed text-steel-dark/85" data-id="element-234" data-mp="26mna0m2jt0ln">
              Boards, nominating and governance committees, audit and technology committees, private equity operating
              partners, and CEOs evaluating a technology leader for oversight roles in capital-intensive, regulated
              environments. The record below is drawn entirely from delivered outcomes inside a $2B revenue enterprise
              managing $10B+ in regulated assets — every claim carries the number that proves it.
            </p>
            <h3 className="display mt-7 text-xl font-black text-steel-dark" data-id="element-235" data-mp="16jmoi07tio39">How to read it</h3>
            <p className="mt-3 text-[15px] leading-relaxed text-steel-dark/85" data-id="element-236" data-mp="1alq0cpyefup2">
              Section 02 covers the oversight and capital-allocation posture a director brings to the table. Sections
              03 through 05 are the operating proof: realized value, executed infrastructure, and technology risk under
              control. Section 06 addresses the organizational capacity behind the results, and Section 07 defines the
              engagement terms.
            </p>
          </Panel>
          <Panel tone="steel" className="p-7" data-id="element-237" data-mp="1ybu3yxbl7ug2">
            <h3 className="display text-xl font-black text-panel" data-id="element-238" data-mp="1yd57qx519276">Council & Industry Standing</h3>
            <ul className="mt-4 space-y-4 text-[15px] leading-relaxed text-panel/80" data-id="element-239" data-mp="23c8d6ioxdafo">
              <li data-id="element-240" data-mp="2ddkcrgbo05cg">Active member, Gartner CIO Council</li>
              <li data-id="element-241" data-mp="28ehce96mqnat">Active member, Unite CIO Council</li>
              <li data-id="element-242" data-mp="28h2emzaub593">Frequent industry speaker on AI governance and cybersecurity</li>
              <li data-id="element-243" data-mp="23mptmktptlv8">Published author on generation expansion and reclaimed water supply strategy</li>
              <li data-id="element-244" data-mp="1zm5uxcthua6z">Top skills: Business Strategy · SAP Products · Board Governance</li>
            </ul>
          </Panel>
        </div>
      </section>
    </div>;
}